import { createContext, useContext, useEffect, useState, useCallback } from "react";
import { listDownloads, totalCachedBytes } from "@/lib/offlineCache";

/**
 * App-wide offline state:
 *   - online: whether the browser is online right now
 *   - downloads: array of { code, n, name, size_bytes, downloaded_at }
 *   - totalBytes: sum across downloads
 *   - refresh(): force re-read from IndexedDB
 */
const OfflineContext = createContext({
  online: true,
  downloads: [],
  downloadedCodes: new Set(),
  totalBytes: 0,
  refresh: () => {},
});

export function OfflineProvider({ children }) {
  const [online, setOnline] = useState(
    typeof navigator !== "undefined" ? navigator.onLine !== false : true
  );
  const [downloads, setDownloads] = useState([]);
  const [totalBytes, setTotalBytes] = useState(0);

  const refresh = useCallback(async () => {
    try {
      const rows = await listDownloads();
      setDownloads(rows);
      setTotalBytes(await totalCachedBytes());
    } catch (e) {
      // IndexedDB might be blocked (incognito / Safari ITP) — fail gracefully.
      // eslint-disable-next-line no-console
      console.warn("offline: could not read IndexedDB", e?.message);
    }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  useEffect(() => {
    const goOnline = () => setOnline(true);
    const goOffline = () => setOnline(false);
    window.addEventListener("online", goOnline);
    window.addEventListener("offline", goOffline);
    return () => {
      window.removeEventListener("online", goOnline);
      window.removeEventListener("offline", goOffline);
    };
  }, []);

  const downloadedCodes = new Set(downloads.map((d) => d.code));

  return (
    <OfflineContext.Provider value={{ online, downloads, downloadedCodes, totalBytes, refresh }}>
      {children}
    </OfflineContext.Provider>
  );
}

export const useOffline = () => useContext(OfflineContext);
